import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { PHONES } from '../content';

const HOMEPAGE_FAQS = [
  {
    q: 'Kocaeli\'de en yakın lastikçi nasıl bulurum?',
    a: '7/24 Lastik\'i arayarak Kocaeli genelinde en yakın mobil lastikçi hizmetine ulaşabilirsiniz. Ekibimiz bulunduğunuz konuma ortalama 30 dakikada gelir. Sabit bir adrese gitmenize gerek yoktur — biz size geliriz.',
  },
  {
    q: 'Lastik patladı ne yapmalıyım?',
    a: 'Öncelikle güvenli bir şekilde yol kenarına çekin ve dörtlü flaşörleri yakın. Ardından bizi arayın: 0534 968 7611. Ekibimiz en kısa sürede yanınızda olacak. Lastik tamiri veya değişimi yerinde yapılır.',
  },
  {
    q: '7/24 lastik yol yardım hizmeti gerçekten 24 saat mi?',
    a: 'Evet, 365 gün 24 saat kesintisiz hizmet veriyoruz. Gece yarısı, hafta sonu, resmi tatil — fark etmez. Kocaeli\'nin tüm ilçelerine her an ulaşıyoruz.',
  },
  {
    q: 'Lastik yol yardım ücreti ne kadar?',
    a: 'Ücret yapılacak işleme göre değişir. Basit bir lastik tamiri ile komple lastik değişimi farklı fiyatlandırılır. Bizi aradığınızda durumunuza göre net fiyat bilgisi veririz. Ek veya gizli ücret yoktur.',
  },
  {
    q: 'Kocaeli\'nin hangi ilçelerine hizmet veriyorsunuz?',
    a: 'İzmit, Gebze, Darıca, Körfez, Gölcük, Derince, Çayırova, Kartepe, Başiskele, Karamürsel, Kandıra, Dilovası ve Hereke dahil tüm Kocaeli genelinde mobil lastik yol yardım hizmeti sunuyoruz.',
  },
  {
    q: 'Kamyon veya TIR lastik tamiri de yapıyor musunuz?',
    a: 'Evet, binek araçtan kamyona, otobüsten traktöre kadar her tip araca hizmet veriyoruz. Ağır vasıta lastik tamiri ve değişimi de mobil ekibimiz tarafından yerinde yapılır.',
  },
  {
    q: 'Otoyolda lastik patladı, gelir misiniz?',
    a: 'Evet, TEM Otoyolu ve D-100 Karayolu dahil tüm otoyol güzergahlarına müdahale ediyoruz. Güvenli bir noktaya çekip bizi arayın, en yakın ekibimizi yönlendiririz.',
  },
  {
    q: 'Lastik değişiminde garanti var mı?',
    a: 'Evet, tüm lastik değişimi ve tamir işlemlerimizde 24 ay garanti sunuyoruz. Kaliteli malzeme kullanır, işçilik garantisi veririz.',
  },
  {
    q: 'Kış lastiği ne zaman takılmalı Kocaeli?',
    a: 'Kocaeli\'de kış lastiği Kasım ortasından itibaren takılmalıdır. Hava sıcaklığı sürekli 7°C altında olduğunda yaz lastiğinin tutunması azalır. Kartepe dağ yollarına çıkacaksanız Kasım başı idealdir.',
  },
  {
    q: 'Lastik basıncı kaç olmalı?',
    a: 'Lastik basıncı aracınızın kapı eşiğindeki etikette yazar. Genellikle 2.2 — 2.5 bar arasındadır. Düşük basınçla sürüş lastik ömrünü kısaltır ve yakıt tüketimini artırır. Ücretsiz yerinde basınç kontrolü yapıyoruz.',
  },
  {
    q: 'Stepnem yoksa ne yapmalıyım?',
    a: 'Stepneniz yoksa veya stepneniz de inmiş durumdaysa, bizi arayın. Aracınıza uygun yeni lastik getirip yerinde montaj yapıyoruz. Çekiciye gerek kalmaz.',
  },
  {
    q: 'WhatsApp\'tan konum gönderebilir miyim?',
    a: 'Evet! WhatsApp üzerinden canlı konumunuzu göndermeniz en hızlı yöntemdir. Ekibimiz konumunuza göre en kısa rotayı belirler. WhatsApp: 0534 968 7611.',
  },
];

export default function HomepageFAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": HOMEPAGE_FAQS.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="wrap">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div className="pill" style={{ marginBottom: '1rem' }}>SSS</div>
          <h2 className="h2">
            Sık Sorulan <span style={{ color: 'var(--blue)' }}>Sorular</span>
          </h2>
          <p style={{ color: 'var(--text2)', fontSize: '.875rem', maxWidth: 480, margin: '.75rem auto 0', lineHeight: 1.7 }}>
            Kocaeli lastik yol yardım hakkında merak edilenler
          </p>
        </div>

        <div style={{ maxWidth: 740, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '.625rem' }}>
          {HOMEPAGE_FAQS.map((faq, i) => {
            const isOpen = openIndex === i;
            return (
              <motion.div
                key={faq.q}
                data-testid={`homepage-faq-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .35, delay: i * .05 }}
                style={{
                  background: 'var(--card)',
                  border: `1px solid ${isOpen ? 'var(--blue-border)' : 'var(--border)'}`,
                  borderRadius: 'var(--r)',
                  overflow: 'hidden',
                  transition: 'border-color .25s',
                  cursor: 'pointer',
                }}
                onClick={() => setOpenIndex(isOpen ? null : i)}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '1.125rem 1.375rem', gap: '1rem',
                }}>
                  <h3 style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                    fontSize: '.9rem', color: 'var(--text)', lineHeight: 1.4, margin: 0,
                  }}>
                    {faq.q}
                  </h3>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: .25 }}
                    style={{ flexShrink: 0, color: 'var(--muted)' }}
                  >
                    <ChevronDown size={18} />
                  </motion.div>
                </div>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: .25 }}
                    >
                      <div style={{
                        padding: '0 1.375rem 1.25rem',
                        fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.75,
                      }}>
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
          <p style={{ color: 'var(--text2)', fontSize: '.85rem', marginBottom: '1rem' }}>
            Başka sorunuz mu var? Hemen arayın!
          </p>
          <a
            href={PHONES.main_tel}
            data-testid="faq-call-cta"
            className="btn btn-primary btn-lg ring num"
            style={{ gap: '.5rem' }}
          >
            <Phone size={16} /> {PHONES.main}
          </a>
        </div>
      </div>
    </section>
  );
}
